import type { TranslationSchema } from '../types';

export const en: TranslationSchema = {
  nav: {
    features: 'Capabilities',
    dlpVault: 'DLP Vault',
    budget: 'Budget Engine',
    analytics: 'Analytics',
    architecture: 'Architecture',
    comparison: 'Comparison',
    faq: 'FAQ',
    github: 'GitHub',
    joinWaitlist: 'Join Waitlist',
    badge: 'Private Beta',
    openSourceBadge: 'Open Source',
  },
  hero: {
    statusBadge: 'Private Beta Access Opening Monthly',
    openSourceBadge: '100% Open Source on GitHub',
    titleLine1: 'The Open-Source Enterprise AI Gateway with',
    titleHighlight: 'Zero-Trust Data Sovereignty',
    titleLine2: 'and Precision Budget Controls',
    description:
      'Safely empower your teams to use any LLM. In-memory DLP sanitization redacts PII and secrets before egress, backed by strict USD team budgets with soft-overdraft handling, SAML SSO, and immutable audit logs.',
    trustPoint1: 'Zero-Storage Ephemeral Token Vault',
    trustPoint2: 'Universal Multi-Provider Fallback',
    trustPoint3: 'Sub-millisecond Edge Gateway Routing',
    githubStars: 'Star on GitHub',
  },
  waitlist: {
    inputPlaceholder: 'Enter your work email...',
    ctaButton: 'Request Early Access',
    submitting: 'Securing Your Spot...',
    successTitle: 'You are on the list!',
    successMessage:
      'Thank you for joining our private beta waitlist. We will notify you when the next monthly cohort opens.',
    duplicateMessage:
      "You're already registered! We have your email on file and will reach out in the next cohort.",
    privacyNotice: 'Strict zero-spam policy. Your email is exclusively stored for beta invitations.',
    errorMessage: 'Unable to register email. Please verify your address and try again.',
    statsNote: 'Joining 450+ engineering & security teams waiting for beta access',
    rolloutNotice: 'Cohort invitations are sent on a monthly schedule.',
  },
  dlpDemo: {
    badge: 'Live Interactive Demonstration',
    title: 'Experience Bi-directional DLP Sanitization',
    subtitle:
      'Sensitive credentials and customer PII are tokenized in-memory before leaving your firewall, then restored automatically on response streaming.',
    samplePromptLabel: 'Load Sample Sensitive Prompt:',
    presets: {
      card: '💳 Customer Credit Card & Billing',
      apiSecret: '🔑 Production API Key & Secret',
      pii: '👤 Employee PII & National ID (SSN)',
    },
    rawInputLabel: '1. User Prompt (Internal Enterprise Perimeter)',
    rawInputPlaceholder: 'Type a prompt with emails, credit cards, or API keys...',
    sanitizedLabel: '2. Sanitized Prompt (Transmitted to External LLM Provider)',
    sanitizedSubtext: 'PII & Secrets replaced with cryptographic surrogate tokens',
    restoredLabel: '3. LLM Response (Re-anonymized in Browser/App)',
    restoredSubtext: 'Dynamic stream replacement restores original context seamlessly',
    vaultStatus: 'In-Memory Vault Status: ACTIVE (Ephemeral, Never Persisted)',
    redactedBadge: 'Detected & Redacted',
    zeroStorageBadge: '100% Zero-Leak Guarantee',
  },
  budgetSimulator: {
    badge: 'Financial Guardrails',
    title: 'Precision USD Budget & Soft-Overdraft Engine',
    subtitle:
      'Enforce atomic dollar limits per team. If a prompt slightly exceeds remaining funds, Workouse lets the request finish gracefully, records the negative balance, and prevents subsequent overruns.',
    teamBudgetLabel: 'Team Monthly Budget Cap (USD):',
    teamBudgetSub: 'Canonical limits are denominated in USD (min $5.00) with automatic currency conversions.',
    currencyLabel: 'Display Currency:',
    currentBalanceLabel: 'Remaining Balance',
    spentLabel: 'Total Spent This Cycle',
    simulatePromptButton: '⚡ Simulate $0.05 LLM Request',
    simulateOverdraftButton: '🚨 Trigger Soft-Overdraft ($0.20 Request)',
    resetButton: 'Reset Simulation',
    overdraftBannerTitle: 'Soft-Overdraft Activated (Negative Balance Recorded)',
    overdraftBannerDesc:
      'Request completed successfully so user work was not broken. Balance is now negative; subsequent requests are blocked until budget replenishment.',
    statusActive: 'ACTIVE (Within Budget)',
    statusOverdraft: 'OVERDRAFT BLOCKED (Replenishment Required)',
    ruleMinLimit: 'Minimum Team Budget: $5.00 USD',
    ruleSoftOverdraft: 'Soft Overdraft: Completes turn, records negative balance, halts new runs',
  },
  analytics: {
    badge: 'Observability & Auditability',
    title: 'Immutable Token & Cost Analytics',
    subtitle:
      'Every single token consumed through the gateway is logged with cryptographic attribution. Real-time cost breakdown across OpenAI, Claude, and local models.',
    metrics: {
      tokens: 'Total Tokens Processed',
      spend: 'Monthly Cloud Spend',
      dlpBlocked: 'PII Entities Redacted',
      latency: 'Avg Gateway Latency',
    },
    liveAuditTitle: 'Append-Only Immutable Audit Log Stream',
    liveAuditSub: 'Audit events are written directly to PostgreSQL with tamper-proof attribution.',
    tableHeaders: {
      time: 'Timestamp',
      model: 'Model / Gateway',
      team: 'Team / User',
      tokens: 'Tokens (In/Out)',
      cost: 'Cost (USD)',
      dlpFlag: 'DLP Vault Action',
    },
    modelShareTitle: 'Provider Cost Distribution',
    immutableNotice: '100% Append-Only Architecture: Logs can never be altered or deleted.',
  },
  features: {
    badge: 'Enterprise Architecture',
    title: 'Built for Security Officers, Loved by Engineers',
    subtitle:
      'A comprehensive AI control plane built with strict functional programming contracts, zero data leakage, and airtight financial guardrails.',
    items: [
      {
        id: 'dlp',
        tag: 'Data Sovereignty',
        title: 'In-Memory DLP Token Vault',
        description:
          'Tier 2 organization regex rules execute alongside universal detectors (credit cards, SSNs, AWS keys, JWTs). Tokens live strictly in volatile memory per request.',
        specs: ['Tier 2 Overrides Tier 1', 'Client-side Stream Deanonymizer', 'Zero DB / Log Leaks'],
      },
      {
        id: 'budget',
        tag: 'Financial Guardrails',
        title: 'Precision USD Budget Engine',
        description:
          'Enforce hard limits per team in canonical USD ($5.00 min limit). Seamless soft-overdraft handling finishes current request, logs negative balance, and prevents overrun.',
        specs: ['USD Canonical Quotas', 'Auto Local Currency Display (TRY/EUR/GBP)', 'Real-time Cost Accounting'],
      },
      {
        id: 'gateway',
        tag: 'High Availability',
        title: 'Unified AI Gateway Router',
        description:
          'Automatic cascading failover: Team Gateway → Org Gateway → Platform Default. Native support for Portkey, Cloudflare AI Gateway, OpenAI, Anthropic, and local Ollama.',
        specs: ['AES-256-GCM Key Encryption', 'Smart Fallback Cascade', 'Latency & Token Analytics'],
      },
      {
        id: 'rag',
        tag: 'Knowledge Base',
        title: 'Async Parser & RAG Engine',
        description:
          'Haskell Parsec document extraction worker converts PDF/DOCX/Markdown to structured AST asynchronously. Vectors isolated strictly by organization ID in Qdrant.',
        specs: ['100% Async Queue Processing', 'Presigned R2/S3 Direct Upload', 'Strict Org Vector Partitioning'],
      },
      {
        id: 'iam',
        tag: 'Identity & Compliance',
        title: 'Enterprise IAM & Audit Seam',
        description:
          'NextAuth v5 identity federation supporting SAML Jackson, LDAP, and OAuth with post-login multi-tenant organization selector. Immutable audit logs for every token.',
        specs: ['SAML 2.0 & LDAPts Support', 'Post-Login Org Selection', 'Immutable Append-Only Audit Trail'],
      },
      {
        id: 'deployment',
        tag: 'Deployment Flexibility',
        title: 'Cloudflare SaaS or Self-Hosted',
        description:
          'Run as edge-first Multi-Tenant SaaS on Cloudflare Pages + Tunnel + R2, or deploy inside your corporate network as a standalone Docker Compose stack.',
        specs: ['Zero-Trust Cloudflare Tunnel', 'Docker Compose Single-Tenant', 'PostgREST Edge DB Layer'],
      },
      {
        id: 'connectors',
        tag: 'Extensibility',
        title: 'Connector Plugins & WAP Protocol',
        description:
          'Standardized Workouse Action Protocol (WAP) connects enterprise apps like Paraşüt accounting, CRM systems, and arbitrary OpenAPI 3.0 REST endpoints safely.',
        specs: ['Workouse Action Protocol (WAP)', 'Pre-built Paraşüt & CRM Plugins', 'OpenAPI 3.0 Auto-Scaffolding'],
      },
      {
        id: 'sandbox',
        tag: 'Safe Execution',
        title: 'Isolated V8 Sandboxes & Reusable Apps',
        description:
          'Run agentic worker scripts in isolated Cloudflare V8 / Deno sandboxes. Two-phase Human-in-the-Loop simulation renders visual diffs before committing state-mutating actions.',
        specs: ['Isolated Cloudflare V8 / Deno', 'Two-Phase HITL Dry-Run Diffs', 'Saved Workflow Apps & Slash Commands'],
      },
      {
        id: 'secrets',
        tag: 'Authoritative Security',
        title: 'Authoritative Secrets Architecture',
        description:
          'Zero raw credentials ingress. HashiCorp Vault on-prem and Cloudflare Secrets Store on edge ensure untrusted sandboxes and LLMs never access unencrypted tokens.',
        specs: ['HashiCorp Vault & Cloudflare Secrets', 'Zero Raw Credential Ingress', 'Broker-Proxied Egress'],
      },
    ],
  },
  architecture: {
    badge: 'Data Flow Topology',
    title: 'How Workouse AI Protects Your Organization',
    subtitle:
      'Real text stays in your PostgreSQL perimeter. Only sanitized surrogate tokens leave your network.',
    step1Title: '1. User Prompt Submitted',
    step1Desc: 'Employee submits prompt via Chat UI, IDE extension, or API with SAML authentication.',
    step2Title: '2. In-Memory DLP Sanitization',
    step2Desc: 'Volatile Token Vault matches PII/secrets and replaces them with surrogate tokens {{TAG_N}}.',
    step3Title: '3. Encrypted Gateway Egress',
    step3Desc: 'Sanitized request routes through AI Gateway to OpenAI, Anthropic, or Cloudflare AI.',
    step4Title: '4. Dynamic Response Re-anonymization',
    step4Desc: 'LLM response streams back and is re-anonymized in-flight. Tokens are discarded from RAM.',
    securityNote: 'Zero PII reaches external model providers. Secrets are never logged to disks or telemetry.',
  },
  comparison: {
    badge: 'Enterprise Comparison',
    title: 'Why Direct LLM Access Fails Enterprise Compliance',
    subtitle:
      'See how Workouse AI provides the safety, governance, and cost controls missing from raw LLM subscriptions.',
    headers: {
      feature: 'Capability',
      direct: 'Direct LLM Accounts',
      genericWrapper: 'Generic AI Wrappers',
      workouse: 'Workouse AI Gateway',
    },
    rows: [
      {
        feature: 'In-Memory Bi-directional DLP Redaction',
        direct: 'None (Full Data Leakage)',
        genericWrapper: 'Basic regex / stored in DB',
        workouse: 'Ephemeral In-Memory Token Vault (Zero Leaks)',
        highlight: true,
      },
      {
        feature: 'Team Budget & Overdraft Enforcement',
        direct: 'Account-wide only',
        genericWrapper: 'Manual monthly billing',
        workouse: 'Atomic USD Caps + Soft-Overdraft Guard',
        highlight: true,
      },
      {
        feature: 'Multi-Model Fallback & Gateway Routing',
        direct: 'Single vendor lock-in',
        genericWrapper: 'Fixed vendor list',
        workouse: 'Portkey + Cloudflare + Direct + Ollama',
      },
      {
        feature: 'Enterprise SSO & IAM',
        direct: 'Enterprise addon $$$',
        genericWrapper: 'Email/Password only',
        workouse: 'SAML 2.0, LDAP & Post-Login Org Selector',
      },
      {
        feature: 'Self-Hosted Air-Gapped Deployment',
        direct: 'Not Available',
        genericWrapper: 'Cloud Only',
        workouse: 'Full Docker Compose Air-Gapped Stack',
      },
      {
        feature: 'Immutable Usage & Token Audit Logs',
        direct: 'Basic API history',
        genericWrapper: 'Basic dashboard',
        workouse: 'Cryptographic Append-Only Attribution',
      },
    ],
  },
  cta: {
    badge: 'Exclusive Private Beta',
    title: 'Secure Your Organization’s AI Workflows Today',
    description:
      'Join leading technology, healthcare, and finance engineering leaders deploying compliant, budget-safe AI with Workouse.',
    button: 'Join the Waitlist',
    disclaimer: 'No credit card required. Early access cohort invitations roll out monthly.',
    githubButton: 'Explore on GitHub',
  },
  faq: {
    badge: 'Frequently Asked Questions',
    title: 'Everything You Need to Know',
    subtitle: 'Have questions about deployment, security, or the monthly rollout?',
    items: [
      {
        question: 'Is Workouse AI open-source?',
        answer:
          'Yes! Workouse AI is open-core and transparent. The core gateway, in-memory DLP tokenizer, and budget accounting engine are open-source on GitHub (github.com/workouse). You can self-host it on Docker Compose or run the multi-tenant Cloudflare edge edition.',
      },
      {
        question: 'How does the DLP Token Vault prevent data leakage to LLM providers?',
        answer:
          'Before any request leaves your secure perimeter, our in-memory tokenizer parses the prompt against Tier 1 (universal patterns like credit cards, SSNs, API tokens) and Tier 2 (custom org regex) rules. Sensitive strings are replaced with surrogate tokens (e.g. {{CREDIT_CARD_1}}). The mapping exists only in ephemeral memory and is destroyed immediately after response deanonymization.',
      },
      {
        question: 'Can Workouse AI be deployed fully on-premises in our private cloud?',
        answer:
          'Yes! Mode A is a single-tenant self-hosted Docker Compose environment suitable for private corporate VPCs or air-gapped networks. Mode B is a high-performance Cloudflare Pages + Tunnel + R2 multi-tenant SaaS deployment.',
      },
      {
        question: 'How does the budget soft-overdraft protection work?',
        answer:
          'Teams are assigned canonical USD budgets (minimum $5.00). If a user initiates a request that slightly exceeds the remaining balance (e.g. $0.20 cost with $0.05 remaining), Workouse allows the response to complete smoothly, records the negative balance (-$0.15), and automatically blocks subsequent prompts until replenished.',
      },
      {
        question: 'Which LLM providers and gateways are supported?',
        answer:
          'Workouse AI natively integrates with Portkey AI Gateway, Cloudflare AI Gateway, direct provider APIs (OpenAI, Anthropic, Google Gemini), and self-hosted open-weight models running on Ollama / vLLM.',
      },
      {
        question: 'How often are private beta invitations sent?',
        answer:
          'We review and admit private beta cohorts on a monthly rolling schedule. Priority access is given to corporate domains with team collaboration requirements.',
      },
    ],
  },
  seo: {
    title: 'Workouse AI Gateway — Zero-Trust Enterprise AI Sovereignty & DLP Vault',
    description:
      'Deploy Workouse AI: the zero-trust enterprise AI gateway with real-time in-memory DLP token vault, USD budget guardrails, and unified multi-provider routing. Join the monthly private beta cohort.',
    keywords:
      'AI Gateway, Enterprise AI Gateway, Data Loss Prevention, In-Memory DLP, LLM Proxy, AI Cost Guardrails, Data Sovereignty, Self-Hosted AI Gateway, OpenAI Proxy, Anthropic Proxy, Portkey Gateway, AI Security',
    ogTitle: 'Workouse AI Gateway — Zero-Trust Enterprise AI Sovereignty',
    ogDescription:
      'Data sovereignty, ephemeral in-memory DLP tokenization, and USD budget controls for enterprise AI workflows. Self-hosted & cloud ready.',
  },
  footer: {
    tagline: 'Open-Source Enterprise AI Gateway with Zero-Trust Data Sovereignty.',
    dataSovereignty: 'Enterprise Security • ISO 27001 / SOC 2 Ready Architecture',
    openSourceNote: '100% Open Source at github.com/workouse',
    rights: 'All rights reserved.',
    links: {
      architecture: 'Architecture',
      github: 'GitHub',
      docs: 'Documentation',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
    },
  },
};

